/* Next10 — carrossel do hero (fade, paridade com o flexslider do site antigo).
   - autoplay (data-speed) só enquanto o hero está visível; interação do usuário
     (dot, swipe) encerra o autoplay, como o pauseOnAction do site antigo;
   - não pausa no hover (site antigo); pausa no foco por teclado (a11y);
   - slides 2+ chegam dentro de <template> e só entram no DOM quando estão
     prestes a aparecer (LCP = só a 1ª arte). */
class Next10Hero extends HTMLElement {
  connectedCallback() {
    this.slides = Array.from(this.querySelectorAll('.n10-hero__slide'));
    this.dots = Array.from(this.querySelectorAll('.n10-hero__dot'));
    this.pauseButton = this.querySelector('.n10-hero__pause');
    if (this.slides.length === 0) return;

    this.index = 0;
    this.timer = null;
    this.visible = true;
    this.speed = parseInt(this.dataset.speed, 10) || 30000;
    this.reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    this.autoplay = this.dataset.autoplay === 'true' && this.slides.length > 1 && !this.reduceMotion;
    this.paused = !this.autoplay;

    this.dots.forEach((dot, i) => {
      dot.addEventListener('click', () => {
        this.goTo(i);
        this.userStop();
      });
    });

    if (this.pauseButton) {
      this.pauseButton.addEventListener('click', () => this.togglePlay());
    }

    this.addEventListener('focusin', () => this.stop());
    this.addEventListener('focusout', () => this.resume());

    this.bindSwipe();
    this.bindVisibility();
    this.bindEditor();

    this.goTo(0);
    this.schedulePrefetch();
    if (this.autoplay) this.start();
  }

  disconnectedCallback() {
    this.stop();
    if (this.observer) this.observer.disconnect();
    if (this.onBlockSelect) {
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('shopify:block:deselect', this.onBlockDeselect);
    }
  }

  /* Tira a mídia do <template> e a coloca no DOM (uma vez por slide). */
  hydrate(index) {
    const slide = this.slides[index];
    if (!slide) return;
    const tpl = slide.querySelector('template.n10-hero__lazy');
    if (!tpl) return;
    const content = tpl.content.cloneNode(true);
    content.querySelectorAll('img').forEach((img) => {
      img.loading = 'eager';
    });
    tpl.parentNode.insertBefore(content, tpl);
    tpl.remove();
  }

  /* Prepara o próximo slide com antecedência (imagem e, se houver, vídeo). */
  prefetch(index) {
    const total = this.slides.length;
    const i = ((index % total) + total) % total;
    if (i === this.index) return;
    this.hydrate(i);
    const saveData = navigator.connection && navigator.connection.saveData;
    if (saveData) return;
    this.slides[i].querySelectorAll('video').forEach((video) => {
      if (video.getClientRects().length === 0 || video.preload === 'auto') return;
      video.preload = 'auto';
      video.load();
    });
  }

  /* Só depois de a arte do 1º slide carregar, para não concorrer com o LCP. */
  schedulePrefetch() {
    if (this.slides.length < 2) return;
    const first = this.slides[0].querySelector('img');
    const run = () => {
      const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 250));
      idle(() => this.prefetch(this.index + 1));
    };
    if (!first || first.complete) {
      run();
    } else {
      first.addEventListener('load', run, { once: true });
      first.addEventListener('error', run, { once: true });
    }
  }

  goTo(index) {
    const total = this.slides.length;
    const previous = this.index;
    this.index = ((index % total) + total) % total;
    this.hydrate(this.index);

    this.slides.forEach((slide, i) => {
      const current = i === this.index;
      slide.classList.toggle('is-active', current);
      slide.setAttribute('aria-hidden', current ? 'false' : 'true');
      slide.querySelectorAll('video').forEach((video) => {
        if (current) {
          this.playVideo(video);
        } else {
          video.pause();
        }
      });
    });

    this.dots.forEach((dot, i) => {
      const current = i === this.index;
      dot.classList.toggle('is-active', current);
      dot.setAttribute('aria-selected', current ? 'true' : 'false');
    });

    if (previous !== this.index) this.prefetch(this.index + 1);
  }

  playVideo(video) {
    if (video.getClientRects().length === 0) return; // variante escondida (mobile/desktop)
    video.muted = true;
    const attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') attempt.catch(() => {});
  }

  next() {
    this.goTo(this.index + 1);
  }

  start() {
    if (this.slides.length < 2 || this.paused || !this.visible) return;
    this.stop();
    this.timer = setInterval(() => this.next(), this.speed);
  }

  stop() {
    if (this.timer) clearInterval(this.timer);
    this.timer = null;
  }

  resume() {
    if (!this.paused) this.start();
  }

  /* pauseOnAction do site antigo: depois de o usuário navegar, o hero não roda mais sozinho. */
  userStop() {
    if (this.paused) return;
    this.paused = true;
    this.stop();
    this.reflectPaused();
  }

  togglePlay() {
    this.paused = !this.paused;
    this.reflectPaused();
    if (this.paused) {
      this.stop();
    } else {
      this.start();
    }
  }

  reflectPaused() {
    this.classList.toggle('n10-hero--paused', this.paused);
    if (this.pauseButton) {
      this.pauseButton.setAttribute('aria-pressed', this.paused ? 'true' : 'false');
    }
  }

  bindSwipe() {
    let startX = null;
    let startY = null;

    this.addEventListener(
      'touchstart',
      (event) => {
        startX = event.touches[0].clientX;
        startY = event.touches[0].clientY;
      },
      { passive: true }
    );

    this.addEventListener(
      'touchend',
      (event) => {
        if (startX === null) return;
        const dx = event.changedTouches[0].clientX - startX;
        const dy = event.changedTouches[0].clientY - startY;
        if (Math.abs(dx) > 50 && Math.abs(dx) > Math.abs(dy)) {
          this.goTo(dx < 0 ? this.index + 1 : this.index - 1);
          this.userStop();
        }
        startX = null;
        startY = null;
      },
      { passive: true }
    );
  }

  bindVisibility() {
    // Não gasta bateria nem banda enquanto o hero está fora da tela.
    this.observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        this.visible = entry.isIntersecting;
        if (this.visible) {
          this.resume();
        } else {
          this.stop();
        }
      });
    });
    this.observer.observe(this);
  }

  /* Editor de temas: selecionar um bloco mostra o slide e segura o autoplay. */
  bindEditor() {
    if (!window.Shopify || !window.Shopify.designMode) return;
    this.onBlockSelect = (event) => {
      const slide = event.target && event.target.closest ? event.target.closest('.n10-hero__slide') : null;
      if (!slide || !this.contains(slide)) return;
      this.goTo(this.slides.indexOf(slide));
      this.stop();
    };
    this.onBlockDeselect = (event) => {
      if (event.target && this.contains(event.target)) this.resume();
    };
    document.addEventListener('shopify:block:select', this.onBlockSelect);
    document.addEventListener('shopify:block:deselect', this.onBlockDeselect);
  }
}

if (!customElements.get('n10-hero')) {
  customElements.define('n10-hero', Next10Hero);
}
